import { useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import Experience from "./canvas/Experience.jsx";
import Configurator from "./ui/Configurator.jsx";
import InformationModal from "./ui/InformationModal.jsx";
import DrawingModal from "./ui/DrawingModal.jsx";
import InspirationModal from "./ui/InspirationModal.jsx";

const NAV_BUTTONS = ["DRAWING", "INFORMATION", "INSPIRATION"];

const DEFAULT_TEXTURE = "/textures/Alpine White W1100 ST9.jpg";

const INITIAL_TEXTURES = {
  handle: null,
  baseUnit: DEFAULT_TEXTURE,
  wallUnit: DEFAULT_TEXTURE,
  tallUnit: DEFAULT_TEXTURE,
  countertop: DEFAULT_TEXTURE,
};

const APP_SETTINGS = window.VMWardrobeConfigurator || {};
const USE_DYNAMIC_DATA =
  APP_SETTINGS.useDynamicData === true || APP_SETTINGS.dataSource === "dynamic";

function captureCanvasPreview(renderer) {
  const canvas = renderer?.domElement;
  if (!canvas) return "";
  try {
    const maxWidth = 520;
    const scale = Math.min(1, maxWidth / canvas.width);
    const width = Math.max(1, Math.round(canvas.width * scale));
    const height = Math.max(1, Math.round(canvas.height * scale));
    const previewCanvas = document.createElement("canvas");
    previewCanvas.width = width;
    previewCanvas.height = height;
    const context = previewCanvas.getContext("2d");
    if (!context) return "";
    context.fillStyle = "#f9fafb";
    context.fillRect(0, 0, width, height);
    context.drawImage(canvas, 0, 0, width, height);
    return previewCanvas.toDataURL("image/jpeg", 0.82);
  } catch {
    return "";
  }
}

export default function App() {
  const rendererRef = useRef(null);
  const [modelPath, setModelPath] = useState(null);
  const [textures, setTextures] = useState(INITIAL_TEXTURES);
  const [totalPrice, setTotalPrice] = useState(0);
  const [wardrobeInfo, setWardrobeInfo] = useState(null);
  const [cartConfig, setCartConfig] = useState(null);
  const [cartStatus, setCartStatus] = useState("");
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDrawingModal, setShowDrawingModal] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [doorsOpen, setDoorsOpen] = useState(false);
  const [showInspirationModal, setShowInspirationModal] = useState(false);
  const [configLoading, setConfigLoading] = useState(USE_DYNAMIC_DATA);

  function handleTextureChange(path) {
    setTextures({
      handle: null,
      baseUnit: path || DEFAULT_TEXTURE,
      wallUnit: path || DEFAULT_TEXTURE,
      tallUnit: path || DEFAULT_TEXTURE,
      countertop: path || DEFAULT_TEXTURE,
    });
  }

  function handleCartConfigChange(config) {
    setCartConfig(config);
    setCartStatus("");
  }

  function handleNavClick(label) {
    if (!modelPath) return;
    if (label === "INFORMATION") setShowInfoModal(true);
    if (label === "DRAWING") setShowDrawingModal(true);
    if (label === "INSPIRATION") setShowInspirationModal(true);
  }

  async function handleAddToCart() {
    if (!cartConfig || !window.VMWardrobe?.addConfiguredWardrobe) return;

    setCartStatus("Adding...");
    try {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const previewImage = captureCanvasPreview(rendererRef.current);
      await window.VMWardrobe.addConfiguredWardrobe({
        ...cartConfig,
        preview_image: previewImage,
      });
      setCartStatus("Added in cart");
    } catch (error) {
      setCartStatus(error.message || "Unable to add to cart");
    }
  }

  return (
    <div className="relative flex flex-col md:flex-row w-full h-full overflow-hidden">
      <div
        className={`relative w-full shrink-0 bg-gray-50 transition-all duration-300 ease-in-out md:h-full ${
          panelCollapsed ? "flex-1 md:w-full" : "h-[45vh] md:w-3/5"
        }`}
      >
        <Canvas
          camera={{ fov: 45, near: 0.1, far: 2000, position: [0, 3, 8] }}
          gl={{ preserveDrawingBuffer: true }}
          onCreated={({ gl }) => {
            rendererRef.current = gl;
          }}
        >
          <Experience
            modelPath={modelPath}
            textures={textures}
            doorsOpen={doorsOpen}
          />
        </Canvas>

        <div className="absolute top-3 left-3 z-10 group">
          <button
            onClick={() => modelPath && setDoorsOpen((v) => !v)}
            className={`relative flex items-center justify-center size-12 md:size-14 rounded-full bg-white shadow-lg hover:shadow-xl transition-shadow ${modelPath ? "cursor-pointer" : "cursor-default"}`}
          >
            <img
              src="/images/ui/closed.png"
              alt=""
              className={`size-7 md:size-9 object-contain ${doorsOpen ? "hidden" : ""}`}
            />
            <img
              src="/images/ui/opened.png"
              alt=""
              className={`size-7 md:size-9 object-contain ${doorsOpen ? "" : "hidden"}`}
            />
          </button>
          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-800 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            {modelPath
              ? doorsOpen
                ? "Close the wardrobe doors"
                : "See what's inside"
              : "Select a wardrobe design"}
          </div>
        </div>

        <button
          onClick={() => setPanelCollapsed((v) => !v)}
          className="absolute right-0 top-1/2 -translate-y-1/2 hidden md:flex items-center justify-center w-4 h-16 z-10 cursor-pointer"
          aria-label={panelCollapsed ? "Expand panel" : "Collapse panel"}
        >
          <div className="w-1.5 h-14 bg-gray-800 rounded-full transition-opacity opacity-60 hover:opacity-100" />
        </button>

        <div className="absolute bottom-4 md:bottom-6 left-0 right-0 flex justify-center gap-2 md:gap-3">
          {NAV_BUTTONS.map((label) => (
            <div key={label} className="relative group">
              <button
                onClick={() => handleNavClick(label)}
                className={`vmwc-primary-button px-3 md:px-5 py-1.5 md:py-2 text-[10px] md:text-xs tracking-widest uppercase rounded-full transition-colors ${
                  modelPath ? "cursor-pointer" : "is-disabled border cursor-default"
                }`}
              >
                {label}
              </button>
              {!modelPath && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  Select a wardrobe design
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div
        className={`border-gray-200 flex flex-col min-h-0 overflow-hidden transition-all duration-300 ease-in-out w-full border-t md:border-t-0 ${
          panelCollapsed
            ? "shrink-0 md:w-0 md:border-0"
            : "flex-1 md:w-2/5 md:border-l"
        }`}
      >
        <button
          onClick={() => setPanelCollapsed((v) => !v)}
          className="flex md:hidden items-center justify-center w-full h-4 shrink-0 cursor-pointer"
          aria-label={panelCollapsed ? "Expand panel" : "Collapse panel"}
        >
          <div className="w-14 h-1.5 bg-gray-800 rounded-full transition-opacity opacity-60 hover:opacity-100" />
        </button>

        <div
          className={`flex-1 overflow-y-auto min-w-[280px] ${panelCollapsed ? "hidden md:block" : ""}`}
        >
          <Configurator
            onModelSelect={setModelPath}
            onTextureChange={handleTextureChange}
            onPriceChange={setTotalPrice}
            onInfoChange={setWardrobeInfo}
            onCartConfigChange={handleCartConfigChange}
            onLoadingChange={setConfigLoading}
          />
        </div>
        <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 bg-white shrink-0 min-w-[280px]">
          <span className="text-sm text-gray-900">
            <span className="font-bold">TOTAL:</span> QAR{" "}
            {totalPrice.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
          <button
            onClick={handleAddToCart}
            disabled={!cartConfig}
            className={`vmwc-primary-button px-4 md:px-5 py-2 text-sm tracking-wide transition-colors ${
              cartConfig ? "cursor-pointer" : "cursor-not-allowed"
            }`}
          >
            {cartStatus || "Add to cart"}
          </button>
        </div>
      </div>

      {configLoading && (
        <div className="vmwc-app-loader" role="status" aria-label="Loading">
          <div className="vmwc-app-loader__mark" aria-hidden="true">
            <span className="vmwc-app-loader__ring" />
            <span className="vmwc-app-loader__core" />
          </div>
        </div>
      )}

      {showInfoModal && (
        <InformationModal
          info={wardrobeInfo}
          onClose={() => setShowInfoModal(false)}
        />
      )}

      {showDrawingModal && (
        <DrawingModal
          info={wardrobeInfo}
          onClose={() => setShowDrawingModal(false)}
        />
      )}

      {showInspirationModal && (
        <InspirationModal
          info={wardrobeInfo}
          onClose={() => setShowInspirationModal(false)}
        />
      )}
    </div>
  );
}
